import {isGuid} from './utils';

export interface OrphanedOption {
    name: string;
    value: any;
}

interface OptionGroup {
    label: string;
    children: any[];
}

/**
 * The value an option contributes to mat-select, mirroring how the select-filter and
 * select-many-filter templates bind it. mat-select compares against this, not the option.
 */
function boundValue(option: any): any {
    return option?.name && option?.value ? option.value : option;
}

/**
 * Describes a stored value that no select option can display, so the caller can render it
 * as a marked option rather than leaving the cell blank.
 *
 * Detection mirrors mat-select's own matching, so a value mat-select can display is never
 * reported. `compare` must be the same function bound to `[compareWith]` and `label` the same
 * one used to write the option text, or the two can disagree and produce either a blank cell
 * or a visibly duplicated option.
 */
export function findOrphanedOption(
    modelValue: any,
    options: any[],
    groupedOptions: OptionGroup[],
    compare: (optionValue: any, value: any) => boolean,
    label: (obj: any, other?: any) => string,
): OrphanedOption | null {
    if (modelValue === null || modelValue === undefined || modelValue === '') {
        return null;
    }

    // A grouped child is bound raw by the template, so it is already its own bound value.
    const children: any[] = (groupedOptions || []).reduce(
        (all: any[], group: OptionGroup) => all.concat(group?.children || []), []);
    const candidates: any[] = [...(options || []).map(boundValue), ...children];
    if (!candidates.length) {
        return null;
    }

    const matched: boolean = candidates.some(candidate => {
        try {
            return compare(candidate, modelValue);
        } catch {
            return false;
        }
    });
    if (matched) {
        return null;
    }

    // A value the comparator cannot see but the list does hold is a comparator limitation,
    // not a retired value.
    if (candidates.some(candidate => candidate === modelValue)) {
        return null;
    }

    const labelled: any = label ? label(modelValue) : modelValue;
    if (typeof labelled !== 'string' && typeof labelled !== 'number') {
        return null;
    }
    const name: string = String(labelled).trim();
    if (!name || isGuid(name)) {
        return null;
    }

    return {name, value: modelValue};
}
